import {memo} from 'react';
import Image from 'next/image';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {
    borderRadius,
    borderWidth,
    colors,
    fontSize,
    fontWeight,
    lineHeight,
    overlay,
    shadows,
    transition,
    typography
} from '@/styles/tokens';

interface RnFitCtaProps {
    heading: string;
    subheading: string;
    bullets: string[];
    buttonText: string;
    openForm: () => void;
    /** Full-bleed artwork behind the card. */
    backgroundImage: string;
}

// Full-bleed artwork behind the card. The gradient underneath shows while it loads.
const sectionSx = {
    position: 'relative',
    overflow: 'hidden',
    py: {xs: 6, md: 8},
    background: `linear-gradient(135deg, ${colors.background.tag} 0%, ${colors.background.visionCard} 100%)`
} as const;

const bgImageStyle = {objectFit: 'cover'} as const;

// Frosted white card: copy on the left, a 2 × 2 grid of pointers on the right. Stacks on phones.
const cardSx = {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', md: 'minmax(0, 5fr) minmax(0, 7fr)'},
    alignItems: 'center',
    gap: {xs: 3, md: 5},
    p: {xs: 3, md: 5},
    bgcolor: overlay.whiteNear,
    backdropFilter: 'blur(8px)',
    border: `${borderWidth.thin} solid ${colors.accent.blueBorder}`,
    borderRadius: borderRadius.xl
} as const;

const headingSx = {
    fontSize: {xs: fontSize[28], md: fontSize[40]},
    lineHeight: {xs: lineHeight[40], md: lineHeight[48]},
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    mb: 1.5
} as const;

const subheadingSx = {...typography.body, color: colors.text.primary, mb: 3} as const;

const pointerGridSx = {
    listStyle: 'none',
    m: 0,
    p: 0,
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))'},
    gap: {xs: 2, md: 3},
    // An odd last pointer spans the row instead of leaving a hole beside it.
    '& > li:last-of-type:nth-of-type(odd)': {gridColumn: {sm: '1 / -1'}}
} as const;

const pointerSx = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.5,
    p: {xs: 2, md: 2.5},
    border: `${borderWidth.thin} solid ${colors.accent.blueBorder}`,
    borderRadius: borderRadius.lg,
    bgcolor: colors.background.panelBlue,
    boxShadow: shadows.card,
    color: colors.text.primary,
    ...typography.body,
    // Hover lifts the chip and fills it brand navy with white copy; the orange tick
    // stays as the accent.
    transition: `transform ${transition.base}, background-color ${transition.base}, border-color ${transition.base}, box-shadow ${transition.base}, color ${transition.base}`,
    '&:hover': {
        transform: 'translateY(-4px)',
        bgcolor: colors.primary.main,
        borderColor: colors.primary.main,
        boxShadow: shadows.lg,
        color: colors.text.inverse
    },
    '@media (prefers-reduced-motion: reduce)': {'&:hover': {transform: 'none'}}
} as const;

// Nudged down so the icon sits on the first line of copy rather than above it.
const pointerIconSx = {color: colors.accent.orange, fontSize: fontSize[20], flexShrink: 0, mt: 0.5} as const;

const RnFitCta = ({heading, subheading, bullets, buttonText, openForm, backgroundImage}: RnFitCtaProps) => (
    <Box sx={sectionSx}>
        <Image src={backgroundImage} alt="" fill sizes="100vw" style={bgImageStyle} />
        <MUIContainer>
            <Box sx={cardSx}>
                <Box>
                    <Typography component="p" className="font-headers" sx={headingSx}>
                        {heading}
                    </Typography>
                    <Typography component="p" className="font-regular-text" sx={subheadingSx}>
                        {subheading}
                    </Typography>
                    <MUIButton
                        label={buttonText}
                        aria-label={buttonText}
                        variant="contained"
                        size="medium"
                        endIcon={<ArrowOutwardIcon />}
                        disableRipple={true}
                        className="button-bg-primary animated-button moving-icon-button"
                        onClick={openForm}
                    />
                </Box>

                <Box component="ul" sx={pointerGridSx}>
                    {bullets.map((point) => (
                        <Box component="li" key={point} className="font-regular-text" sx={pointerSx}>
                            <DoneAllIcon sx={pointerIconSx} aria-hidden="true" />
                            {point}
                        </Box>
                    ))}
                </Box>
            </Box>
        </MUIContainer>
    </Box>
);

export default memo(RnFitCta);
